import {
    putAndHandleAPIError,
    putAndWaitHandleAllAPIError
} from '@api/API.sagas';
import {
    API_GET_CUSTOMER_STATE,
    API_GET_DRAW_DOWN_REPAYMENT_SCHEDULE,
    API_GET_DRAW_DOWN_REPAYMENT_SCHEDULE_SUCCESS,
    API_GET_FORMAL_OFFERS,
    API_PUT_APPLICATION
} from '@api/lendingAPI/LendingAPI.actions';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { OPEN_MODAL } from '@iwModals/Modals.actions';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { fork, put, select, take, takeLatest } from 'redux-saga/effects';
import {
    TrackEarlyRepaymentCalculatorUsed,
    TrackRepaymentScheduleViewed
} from '../../tracking/amplitude.helpers';
import * as actions from './MyOffers.actions';
import {
    getAvailableCash,
    getFlexiLoanFormalOffers,
    getOfferById,
    getProductTypeOfSelectedOffer,
    normaliseRepaymentSchedule
} from './MyOffers.helpers';

export const MY_OFFERS_PAGE_NAME = 'MY_OFFERS';
export const MY_OFFERS_URL = '/my-offers/';

export const myOffersSagaWorkers = [
    fork(activateLoanBuilder),
    fork(getNewDrawdownSchedules),
    fork(getEarlyRepaymentSchedule),
    fork(showRepaymentModal),
    fork(requestNewDrawdownRepaymentScheduleWorker),
    fork(deferredReapplyWorker),
    fork(declinedReapplyWorker)
];

function* activateLoanBuilder() {
    yield takeLatest(actions.ACTIVATE_LOAN_BUILDER, function* () {
        yield put({ type: actions.LOAN_BUILDER_LOADING });

        yield put({ type: API_GET_CUSTOMER_STATE });
        const [getFormalOffersAction] = yield putAndWaitHandleAllAPIError([
            { type: API_GET_FORMAL_OFFERS }
        ]);

        const { formal_offers } = getFormalOffersAction.response.data;

        const { maxCashAmount, flexiLoanFormalOffers, formalOffers } = {
            maxCashAmount: getAvailableCash(formal_offers),
            flexiLoanFormalOffers: getFlexiLoanFormalOffers(formal_offers),
            formalOffers: formal_offers
        };

        const drawdownSchedules = yield* getDrawdownSchedules(
            flexiLoanFormalOffers,
            maxCashAmount
        );

        yield put({
            type: actions.MY_OFFERS_READY,
            formalOffers: formalOffers,
            drawdownSchedules: drawdownSchedules
        });
    });
}

function* getDrawdownSchedules(formalOffers, drawdownAmount) {
    let drawdownSchedules = [];
    for (let i = 0; i < formalOffers.length; i++) {
        const [getRepaymentScheduleAction] = yield putAndWaitHandleAllAPIError([
            {
                type: API_GET_DRAW_DOWN_REPAYMENT_SCHEDULE,
                productType: formalOffers[i].product_type,
                offerId: formalOffers[i].offer_id,
                amount: drawdownAmount
            }
        ]);
        drawdownSchedules.push(
            normaliseRepaymentSchedule({
                formalOffer: formalOffers[i],
                repaymentSchedule: getRepaymentScheduleAction.response.data
            })
        );
    }
    return drawdownSchedules;
}

function* showRepaymentModal() {
    yield takeLatest(
        actions.SHOW_DRAWDOWN_REPAYMENT_SCHEDULE_MODAL,
        function* (action) {
            const { isEarlyRepaymentSchedule, offerId } = action;
            const formalOffers = yield select(
                (state) => state.loanBuilder.formalOffers
            );

            const offer = getOfferById(formalOffers, offerId);

            TrackRepaymentScheduleViewed({
                'CTA source': 'Loan Builder',
                'Product type': offer.product_type,
                'Offer duration': offer.duration.amount,
                'Offered amount': offer.max_amount
            });
            yield put({
                type: actions.MY_OFFERS_REPAYMENT_SCHEDULE_MODAL_DATA,
                isEarlyRepaymentSchedule,
                offerId
            });
            yield put({
                type: OPEN_MODAL,
                id: 'LoanBuilderRepaymentScheduleModal'
            });
        }
    );
}

function* getNewDrawdownSchedules() {
    yield takeLatest(actions.MY_OFFERS_GET_DRAWDOWN_SCHEDULES, function* () {
        yield put({ type: actions.MY_OFFERS_DRAWDOWN_SCHEDULES_LOADING });

        const [formalOffers, drawdownAmount] = yield select((state) => [
            state.loanBuilder.formalOffers,
            state.loanBuilder.drawdownAmount
        ]);

        const drawdownSchedules = yield* getDrawdownSchedules(
            formalOffers,
            drawdownAmount
        );

        yield put({
            type: actions.MY_OFFERS_DRAWDOWN_SCHEDULES_READY,
            newDrawdownSchedules: drawdownSchedules
        });
    });
}

function* getEarlyRepaymentSchedule() {
    yield takeLatest(actions.GET_EARLY_REPAYMENT_BREAKDOWN, function* (action) {
        const { date } = action;
        const { selectedOfferId, drawdownAmount, formalOffers, maxCashAmount } =
            yield select((state) => ({
                selectedOfferId: state.loanBuilder.selectedOfferId,
                drawdownAmount: state.loanBuilder.drawdownAmount,
                formalOffers: state.loanBuilder.formalOffers,
                maxCashAmount: state.loanBuilder.maxCashAmount
            }));

        const selectedProductType = getProductTypeOfSelectedOffer(
            selectedOfferId,
            formalOffers
        );
        TrackEarlyRepaymentCalculatorUsed({
            'User selection': date,
            'Product type': selectedProductType,
            'Offered amount': maxCashAmount
        });

        const [getEarlyRepaymentScheduleAction] =
            yield putAndWaitHandleAllAPIError([
                {
                    type: API_GET_DRAW_DOWN_REPAYMENT_SCHEDULE,
                    productType: selectedProductType,
                    offerId: selectedOfferId,
                    amount: drawdownAmount,
                    date: date
                }
            ]);

        yield put({
            type: actions.SHOW_EARLY_REPAYMENT_BREAKDOWN,
            earlyRepaymentSchedule:
                getEarlyRepaymentScheduleAction.response.data
        });
    });
}

function* requestNewDrawdownRepaymentScheduleWorker() {
    yield takeLatest(
        actions.REQUEST_DRAW_DOWN_REPAYMENT_SCHEDULE,
        function* (action) {
            const { formalOffer, amount } = action;
            yield putAndWaitHandleAllAPIError([
                {
                    type: API_GET_DRAW_DOWN_REPAYMENT_SCHEDULE,
                    productType: formalOffer.product_type,
                    offerId: formalOffer.offer_id,
                    amount
                }
            ]);

            yield take(API_GET_DRAW_DOWN_REPAYMENT_SCHEDULE_SUCCESS);
        }
    );
}

function* deferredReapplyWorker() {
    yield takeLatest(actions.DEFERRED_APPLICATION_REAPPLY, function* (action) {
        const { currentRequestedProduct } = action;

        const isDe = getCountry() === 'DE';

        const durationDataDE = {
            duration: currentRequestedProduct.duration
        };

        const PUTApplicationData = {
            data: {
                requested_products: [
                    {
                        product_type: currentRequestedProduct.product_type,
                        amount: currentRequestedProduct.amount,
                        purpose: currentRequestedProduct.purpose,
                        ...(isDe && durationDataDE)
                    }
                ]
            }
        };

        yield putAndHandleAPIError({
            type: API_PUT_APPLICATION,
            body: JSON.stringify(PUTApplicationData)
        });

        yield put({
            type: REQUEST_CHANGE_ROUTER,
            location: { pathname: '/apply-flexi-loan/' }
        });
    });
}

function* declinedReapplyWorker() {
    yield takeLatest(actions.DECLINED_APPLICATION_REAPPLY, function* (action) {
        const { currentRequestedProduct } = action;

        const isDe = getCountry() === 'DE';

        const durationDataDE = {
            duration: currentRequestedProduct.duration
        };

        const PUTApplicationData = {
            data: {
                requested_products: [
                    {
                        product_type: currentRequestedProduct.product_type,
                        amount: currentRequestedProduct.amount,
                        purpose: currentRequestedProduct.purpose,
                        ...(isDe && durationDataDE)
                    }
                ]
            }
        };

        yield putAndHandleAPIError({
            type: API_PUT_APPLICATION,
            body: JSON.stringify(PUTApplicationData)
        });

        yield put({
            type: REQUEST_CHANGE_ROUTER,
            location: { pathname: '/' }
        });
    });
}
